import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CustomRole {
  id: string;
  name: string;
  description?: string;
  color: string;
  created_at: string;
  updated_at: string;
}

export interface RolePermission {
  id: string;
  role_id: string;
  permission_name: string;
  permission_value: boolean;
  created_at: string;
}

export interface EmployeeRole {
  id: string;
  employee_id: string;
  role_id: string;
  assigned_by?: string;
  assigned_at: string;
}

export const useRoles = () => {
  return useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("custom_roles")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data as CustomRole[];
    },
    staleTime: 5 * 60 * 1000, // 5 دقائق
  });
};

export const useRolePermissions = (roleId?: string) => {
  return useQuery({
    queryKey: ["role-permissions", roleId],
    queryFn: async () => {
      if (!roleId) return [];
      
      const { data, error } = await supabase
        .from("custom_role_permissions")
        .select("*")
        .eq("role_id", roleId);
      
      if (error) throw error;
      return data as RolePermission[];
    },
    enabled: !!roleId,
    staleTime: 2 * 60 * 1000, // دقيقتان
  });
};

export const useEmployeeRoles = (employeeId?: string) => {
  return useQuery({
    queryKey: ["employee-roles", employeeId],
    queryFn: async () => {
      if (!employeeId) return [];
      
      const { data, error } = await supabase
        .from("employee_roles")
        .select(`
          *,
          custom_roles (
            id,
            name,
            description,
            color
          )
        `)
        .eq("employee_id", employeeId);
      
      if (error) throw error;
      return data;
    },
    enabled: !!employeeId,
    staleTime: 2 * 60 * 1000, // دقيقتان
  });
};

export const useCreateRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (roleData: Omit<CustomRole, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase
        .from("custom_roles")
        .insert([roleData])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...roleData }: Partial<CustomRole> & { id: string }) => {
      const { data, error } = await supabase
        .from("custom_roles")
        .update({ ...roleData, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });
};

export const useUpdateRolePermissions = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ roleId, permissions }: { roleId: string; permissions: { permission_name: string; permission_value: boolean }[] }) => {
      // حذف الصلاحيات الحالية
      const { error: deleteError } = await supabase
        .from("custom_role_permissions")
        .delete()
        .eq("role_id", roleId);
      
      if (deleteError) throw deleteError;
      
      // إضافة الصلاحيات الجديدة
      if (permissions.length > 0) {
        const newPermissions = permissions.map(p => ({
          role_id: roleId,
          permission_name: p.permission_name,
          permission_value: p.permission_value
        }));
        
        const { data, error } = await supabase
          .from("custom_role_permissions")
          .insert(newPermissions)
          .select();
        
        if (error) throw error;
        return data;
      }
      
      return [];
    },
    onSuccess: (_, { roleId }) => {
      queryClient.invalidateQueries({ queryKey: ["role-permissions", roleId] });
    },
  });
};

export const useAssignRoleToEmployee = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ employeeId, roleId, assignedBy }: { employeeId: string; roleId: string; assignedBy?: string }) => {
      const { data, error } = await supabase
        .from("employee_roles")
        .insert([{
          employee_id: employeeId,
          role_id: roleId,
          assigned_by: assignedBy
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, { employeeId }) => {
      queryClient.invalidateQueries({ queryKey: ["employee-roles", employeeId] });
      queryClient.invalidateQueries({ queryKey: ["employee-roles"] });
    },
  });
};

export const useRemoveRoleFromEmployee = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ employeeId, roleId }: { employeeId: string; roleId: string }) => {
      const { error } = await supabase
        .from("employee_roles")
        .delete()
        .eq("employee_id", employeeId)
        .eq("role_id", roleId);
      
      if (error) throw error;
    },
    onSuccess: (_, { employeeId }) => {
      queryClient.invalidateQueries({ queryKey: ["employee-roles", employeeId] });
      queryClient.invalidateQueries({ queryKey: ["employee-roles"] });
    },
  });
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (roleId: string) => {
      const { error } = await supabase
        .from("custom_roles")
        .delete()
        .eq("id", roleId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });
};
